<template>
    <div class="payment-schedule">
        <div class="container-width">
            <div class="payment-chart">
                <div v-if="ChartMonthlyPayScheduleGraph" class="tab-content">
                    <div class="row active">
                        <div class="bar-chart">
                            <div class="chart-container">
                                <vue-chart
                                    type="bar"
                                    :data="ChartMonthlyPayScheduleGraph"
                                    :options="tooltipOptions('tooltip-monthly-pay')"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="chart-details">
                <ul class="chart-info">
                    <li><span class="bg-light-green"></span> Principal</li>
                    <li><span class="bg-dark-green"></span> Interest</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';

    export default {
        props: {
            loanData: {
                default: [],
            },
        },
        data() {
            return {
                LastDate: null,
                ChartMonthlyPayScheduleGraph: null,
                YearBalance: [],
            };
        },
        computed: {
            chartMonthlyPaySchedule() {
                const years = [];
                const principalData = [];
                const interestData = [];
                let totalLoanBalance = 0;

                const loanYears = Object.keys(this.loanData);
                loanYears.forEach((year) => {
                    if (year) {
                        years.push(parseInt(year, 10));
                        let principal = 0;
                        let interest = 0;
                        for (let i = 0; i < this.loanData[year].length; i++) {
                            if (this.loanData && this.loanData[year] && this.loanData[year][i]) {
                                principal += this.loanData[year][i].principal;
                                interest += this.loanData[year][i].interestPaid;

                                if (i === this.loanData[year].length - 1) {
                                    totalLoanBalance = this.loanData[year][i].balance;
                                }
                            }
                        }
                        principalData.push(principal);
                        interestData.push(interest);
                        this.YearBalance[year] = totalLoanBalance;
                    }
                });
                return {
                    labels: years,
                    datasets: [
                        {
                            label: 'Principal',
                            backgroundColor: '#08C177',
                            data: principalData,
                            barPercentage: 1,
                        },
                        {
                            label: 'Interest',
                            backgroundColor: '#078181',
                            data: interestData,
                            barPercentage: 1,
                        },
                    ],
                };
            },
        },
        watch: {
            chartMonthlyPaySchedule() {
                this.ChartMonthlyPayScheduleGraph = null;
                setTimeout(() => {
                    this.ChartMonthlyPayScheduleGraph = this.chartMonthlyPaySchedule;
                }, 50);
            },
        },
        mounted() {
            this.ChartMonthlyPayScheduleGraph = this.chartMonthlyPaySchedule;

            document.addEventListener('DOMContentLoaded', () => {
                const canvas = document.getElementById('responsiveCanvas');

                function setCanvasWidth() {
                    if (canvas) {
                        canvas.width = canvas.parentElement.clientWidth;
                    }
                }
                setCanvasWidth();

                window.addEventListener('resize', setCanvasWidth);
            });
        },
        methods: {
            tooltipOptions(type) {
                const totalLoanBalance = this.YearBalance;
                return {
                    legend: {
                        display: false,
                    },
                    gridLines: false,
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback(label) {
                                    return Accounting.formatMoney(`$${label}`, { precision: 0 });
                                },
                                fontSize: window.innerWidth > 480 ? 14 : 10,
                                fontWeight: 'bold',
                            },
                            afterTickToLabelConversion(scaleInstance) {
                                scaleInstance.ticks[scaleInstance.ticks.length - 1] = null;
                            },
                        }],
                        xAxes: [{
                            ticks: {
                                fontSize: window.innerWidth > 480 ? 14 : 10,
                                fontWeight: 'bold',
                            },
                            afterTickToLabelConversion(data) {
                                const xLabels = data.ticks;
                                xLabels.forEach((labels, i) => {
                                    xLabels[i] = `${labels}`;
                                });
                            },
                            gridLines: {
                                drawOnChartArea: false,
                            },
                        }],
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    tooltips: {
                        enabled: false,
                        mode: 'index',
                        custom(tooltip) {
                            let tooltipEl = document.getElementById(type);
                            if (!tooltipEl) {
                                tooltipEl = document.createElement('div');
                                tooltipEl.id = type;
                                tooltipEl.innerHTML = '<div class="graph-tooltip"></div>';
                                this._chart.canvas.parentNode.appendChild(tooltipEl);
                            }
                            if (tooltip.opacity === 0) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }
                            tooltip.displayColors = false;
                            tooltipEl.classList.remove('above', 'below', 'no-transform');
                            if (tooltip.yAlign) {
                                tooltipEl.classList.add(tooltip.yAlign);
                            } else {
                                tooltipEl.classList.add('no-transform');
                            }

                            function getBody(bodyItem) {
                                return bodyItem.lines;
                            }

                            if (tooltip.body) {
                                let innerHtml = '';
                                const titleLines = tooltip.title || [];
                                const bodyLines = tooltip.body.map(getBody);
                                titleLines.forEach((title) => {
                                    innerHtml += `<h5>${title}</h5>`;
                                });

                                const principal = bodyLines[0][0].split(':');
                                innerHtml += `<label>${principal[0]}: <b class="green-text">${Accounting.formatMoney(principal[1], { precision: 2 })}</b></label>`;

                                const interest = bodyLines[1][0].split(':');
                                innerHtml += `<label>${interest[0]}: <b class="blue-text">${Accounting.formatMoney(interest[1], { precision: 2 })}</b></label>`;

                                innerHtml += `<label>Balance: <b>${Accounting.formatMoney(totalLoanBalance[titleLines[0]], { precision: 2 })}</b></label>`;
                                const tableRoot = tooltipEl.querySelector('div');
                                tableRoot.innerHTML = innerHtml;
                            }
                            const positionY = this._chart.canvas.offsetTop;
                            const positionX = this._chart.canvas.offsetLeft;
                            tooltipEl.style.opacity = 1;
                            tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
                            tooltipEl.style.top = `${positionY - 110 + tooltip.caretY}px`;
                            tooltipEl.style.height = '100px';
                        },
                    },
                };
            },
        },
        components: {
            VueChart,
        },
        mixins: [Accounting],
    };
</script>
<style lang="scss" src="../../assets/styles/amortization-breakdown-graph-new.scss"></style>
